import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { useDocumentFile } from 'hooks/imageHooks/useDocFile';
import { FileUpload } from 'components/common/FileUpload';
import { Document } from 'models/DocumentModel';
import AsyncSelect from 'react-select/async';
import { useSearchParams } from 'react-router-dom';
import { Emailer } from 'helpers/EmailerHelipers';


const DocumentForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.DOCUMENT, Code.Form.Name.DOCUMENT];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [issueOptions, setIssueOptions] = Hooks.Form.Options();
  const [formatOptions, setFormatOptions] = Hooks.Form.Options();
  const [folderOptions, setFolderOptions] = Hooks.Form.Options();
  const [recDoctorOptions, setRecDoctorOptions] = Hooks.Form.Options();
  const [sourceOptions, setSourceOptions] = Hooks.Form.Options();
  const [recipientOptions, setRecipientOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Document>({});
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(0); 
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(0); 
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(0); 

  const [recipientTo, setRecipientTo] = useState<string>('');
  const [recipientCC, setRecipientCC] = useState<string>('');
  const [emailTitle, setEmailTitle] = useState<string>('Document Attached');
  const [emailRecipientName, setEmailRecipientName] = useState<string>('');
  const [enableSendButton, setEnableSendButton] = useState<boolean>(false);

  
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [isFileAttached, setIsFileAttached] = useState(false);
  const { handleFileChange, handleFileAttach, handleFileDelete } = useDocumentFile(fileName, setFileName, setIsFileAttached, setError, setMessage);

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const doctorId = searchParams.get(Code.UrlPara.Doctor);
  const examId = searchParams.get(Code.UrlPara.Exam);
  
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_TYPE, Code.Option.Common.SELECT, setTypeOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.API(Code.Form.Name.DOCTOR, Code.Option.Common.ALL, setDoctorOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_ISSUE, Code.Option.Common.NA, setIssueOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_FORMAT, Code.Option.Common.NA, setFormatOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_FOLDER, Code.Option.Common.NA, setFolderOptions, setError);
    Files.Json.Http(Code.File.SOURCE, Code.Option.Common.SELECT, setSourceOptions, setError);
    
   
    API.Data.GetByKeyId<Document>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.document_active!); 
        
        const isValid = await Utility.isValid.File(data.document_file_name);
        setFileName(data.document_file_name);
        setIsFileAttached(isValid);

        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData,
        }));
        setLoading(false);
      }})

      const onNewEntry = async () => {
        if(keyId===Code.Key.NEW && clientId) {
          const clientName = await API.Data.GetClientName(Number(clientId));
          setFormData((prevData) => ({
            ...prevData,
            doctor_id: doctorId ? Number(doctorId) : 2,
            exam_id: examId ? Number(examId) : 2,
            client_id: Number(clientId),
            client_name: Utility.Value.Text(clientName), 
          }));
        }
        setLoading(false);
      };
      onNewEntry();
  }, []);

  useEffect(() => {
    if(selectedSourceId != 0) {
      Files.Json.Http(Code.File.RECIPIENT, Code.Option.Common.SELECT, setRecipientOptions, setError);
      const typeId= formData.type_id;
      if(typeId!=0){
        const docTitle = typeOptions.find(option => option.value == typeId)?.label; 
        setEmailTitle(`${docTitle} Attached` || "Document Attached")
      }
      setSelectedRecipientId(0);
      setSelectedDoctorId(0);
    }
  }, [selectedSourceId]);

  useEffect(() => {
    if (selectedRecipientId == 0) return;

    if (selectedRecipientId == Code.Form.Id.CLIENT) {
      (async () => {
        const clientId = formData.client_id || 0;
        const clientData = await API.Data.GetClient(Number(clientId));
        if(clientData){
          setEmailRecipientName(`${clientData.client_first_name || ''} ${clientData.client_middle_name || ''} ${clientData.client_last_name || ''}` || '');
          if(selectedSourceId == Code.Option.SendSource.EMAIL){
            setRecipientTo(clientData.client_email || '');
          }
          else if(selectedSourceId == Code.Option.SendSource.FAX){
            setRecipientTo(clientData.client_fax || '');
          }
        }
      })();
    }
    else if (selectedRecipientId == Code.Form.Id.RECDOCTOR) {
      Files.Json.API(Code.File.RECDOCTOR, Code.Option.Common.SELECT, setRecDoctorOptions, setError);
    }
  }, [selectedRecipientId]);

  useEffect(() => {
    if (selectedDoctorId == 0) return;
    (async () => {
      const recDoctorData = await API.Data.GetRecDoctor(Number(selectedDoctorId));
      console.log("clientId = reached here",recDoctorData, selectedDoctorId);
      if(recDoctorData){
        setEmailRecipientName(`${recDoctorData.recdoctor_first_name || ''} ${recDoctorData.recdoctor_middle_name || ''} ${recDoctorData.recdoctor_last_name || ''}` || '');
        if(selectedSourceId == Code.Option.SendSource.EMAIL){
          setRecipientTo(recDoctorData.recdoctor_email || '');
        }
        else if(selectedSourceId == Code.Option.SendSource.FAX){
          setRecipientTo(recDoctorData.recdoctor_fax || '');
        }
      }
    })();
  }, [selectedDoctorId]);

  // useEffect(() => {
  //   if(recipientTo || recipientCC) {
  //     if(Utility.isValid.Email(recipientTo) || Utility.isValid.Phone(recipientTo)) setEnableSendButton(true);
  //     else if(Utility.isValid.Email(recipientCC) || Utility.isValid.Phone(recipientTo)) setEnableSendButton(true);
  //     else setEnableSendButton(false);
  //   }
  //   else setEnableSendButton(false);
  // }, [recipientTo, recipientCC]);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
    });

    finalData.form_id = Code.Form.Id.CLIENT;
    finalData.document_file_name = fileName;

    //console.log("-----newid---->",keyId);

    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  const onSendDocByEmailOrFax = () => {
    if(selectedSourceId == 0) return;

    if(selectedSourceId == Code.Option.SendSource.EMAIL){
      if(recipientTo || recipientCC){
        if(!Utility.isValid.Email(recipientTo) && !Utility.isValid.Email(recipientCC)) {
          Utility.Message.Error([`Email id is invalid`], setMessage);
          return;
        }
      }
      else{
        Utility.Message.Error([`Email id is not available`], setMessage);
        return;
      }
      
    }
    else if(selectedSourceId == Code.Option.SendSource.FAX){ 
      if(recipientTo || recipientCC){
        if(!Utility.isValid.Phone(recipientTo) && !Utility.isValid.Phone(recipientCC)) {
          Utility.Message.Error([`Fax number is invalid, must contain exactly 10 digits. Do not include +1`], setMessage);
          return;
        }
      }
      else{
        Utility.Message.Error([`Fax number is not available`], setMessage);
        return;
      }
    }

    if(!emailTitle) {
      Utility.Message.Info([`Title is required before sending.`], setMessage);
      return;
    }

    const companyName = Utility.Storage.CompanyName();
    const userName = Utility.Storage.UserName();
    
    const contentData = Emailer.Basic.Attach({
      title: emailTitle,
      companyName: companyName,
      content: Emailer.Basic.Content(emailRecipientName,formName,companyName,userName)});

    const fileName = formData.document_file_name || '';
    if (fileName) {
      (async () => {
        const fileData = await Files.File.FetchDoc(fileName,"document");
        if(fileData){
          var sendTo = recipientTo;
          var sendCC = recipientCC;
          if(selectedSourceId == Code.Option.SendSource.FAX){
            if(recipientTo) sendTo = `1${recipientTo}@srfax.com`;
            if(recipientCC) sendCC = `1${recipientCC}@srfax.com`;
            const success = await API.Data.sendDocAsFax(fileData, sendTo, sendCC, emailTitle, contentData, setMessage, setError);
            if (success) {
              setRecipientTo('');
              setRecipientCC('');
            }
          }
          else
          {
            const success = await API.Data.sendDocAsEmail(fileData, sendTo, sendCC, emailTitle, contentData, setMessage, setError);
            if (success) {
              setRecipientTo('');
              setRecipientCC('');
            }
          }

          
        }
      })();
    }
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} Privilege={Privilege} print={true} />
          <Control.Tab.Header titles={["Document","More", keyId===Code.Key.NEW ? "" : "Send"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
          
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.FilterSelect id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  setFormData={setFormData}  size={[0,50,1]} require={true} />
                <Field.Select id={Name.Common.IssueId} value={formData.issue_id} title="Issue by" options={issueOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Document.FileNo} value={formData.document_file_no} title={Title.FileNo} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.ClientAsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.SearchOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
                {/* <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} .. loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header> */}
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Document.Name} value={formData.document_name} title={Title.Title} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
              <Control.Box>
                <FileUpload isFileAttached={isFileAttached} handleFileChange={handleFileChange} handleFileAttach={handleFileAttach} handleFileDelete={handleFileDelete} />
              </Control.Box>
            </Control.Tab.Content>
            
            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.Document.RefNo} value={formData.document_refno} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Select id={Name.Common.FormatId} value={formData.format_id} title="Availability" options={formatOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
                <Field.Select id={Name.Common.FolderId} value={formData.folder_id} title="Folder/Rack No." options={folderOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Date id={Name.Document.IssueDate} value={formData.document_issue_date} title={Title.Issue} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Date id={Name.Document.ExpiryDate} value={formData.document_expiry_date} title={Title.Expiry} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Document.Comment} value={formData.document_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
            </Control.Tab.Content>

            {keyId!==Code.Key.NEW && (
              <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
                <Control.Flex>
                  <Control.Column size={[0,40,0,2]}>
                    <Control.Row>
                      <Field.FilterSelect title={"Send Document by"} id={Name.Common.SourceId} value={selectedSourceId} options={sourceOptions} setSelectedId={setSelectedSourceId} />
                    </Control.Row>
                    {selectedSourceId != 0 && (
                      <Control.Row>
                      <Field.FilterSelect title={"Select Recipient"} id={Name.Common.RecipientId} value={selectedRecipientId} options={recipientOptions} setSelectedId={setSelectedRecipientId} />
                      </Control.Row>
                    )}
                    {selectedSourceId != 0 && selectedRecipientId == Code.Form.Id.RECDOCTOR && (
                      <Control.Row>
                        <Field.FilterSelect title={"Select Recipient"} id={Name.RecDoctor.Id} value={selectedDoctorId} options={recDoctorOptions} setSelectedId={setSelectedDoctorId} />
                      </Control.Row>
                    )}
                    {selectedSourceId != 0 && selectedRecipientId != 0 && (selectedRecipientId == Code.Form.Id.CLIENT || selectedDoctorId != 0) && (
                      <Control.Container className='mt2'>
                        <Control.Row>
                          <Field.Text id={"recipientto"} value={recipientTo} title={"To"} onChange={(e) => setRecipientTo(e.target.value)} horizontal={true} width={85}  />
                        </Control.Row>
                        <Control.Row>
                          <Field.Text id={"recipientcc"} value={recipientCC} title={"Cc"} onChange={(e) => setRecipientCC(e.target.value)} horizontal={true} width={85}  />
                        </Control.Row>
                        <Control.Row>
                          <Field.Text id={"mailtitle"} value={emailTitle} title={"Title"} onChange={(e) => setEmailTitle(e.target.value)} horizontal={true} width={85}  />
                        </Control.Row>
                        <Control.Box>
                          <Button.Action title="Send" info={"Click to send document"} onClick={() => onSendDocByEmailOrFax()} className='fright mt1 mb1' />
                        </Control.Box>
                      </Control.Container>
                    )}
                  </Control.Column>
                </Control.Flex>
              </Control.Tab.Content>
            )}
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} print={true} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default DocumentForm;